/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {AfterContentInit, ChangeDetectionStrategy, Component, Injector, Input, OnChanges, OnDestroy, OnInit} from '@angular/core';
import {ReactiveFormsModule, UntypedFormControl} from '@angular/forms';
import {AuthService} from 'compob2b-auth';
import {
  BaseComponent,
  Cart,
  CartEventService,
  CartLocalService,
  CartProduct,
  CartRemoteService,
  ProductCustomer,
  ProductStatuses
} from 'compob2b-libs';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, takeUntil, tap} from 'rxjs/operators';
import {AddToSpecificationPopupComponent} from '../../product/add-to-specification-popup/add-to-specification-popup.component';
import {NbButtonModule, NbInputModule} from '@nebular/theme'
import {TuiIcon} from '@taiga-ui/core'
import {LowerCasePipe} from '@angular/common';
import {DigitOnlyDirective} from '@uiowa/digit-only'

@Component({
  selector: 'app-add-to-cart',
  templateUrl: './add-to-cart.component.html',
  styleUrls: ['./add-to-cart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NbInputModule,
    TuiIcon,
    NbButtonModule,
    DigitOnlyDirective,
    LowerCasePipe,
    ReactiveFormsModule
  ]
})
export class AddToCartComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy, AfterContentInit {

  @Input() product: ProductCustomer;
  @Input() catalog: boolean;
  multiplicityOrder = 1;
  inCart = 0;
  inputFormControl: UntypedFormControl;
  discount: number;
  private inputSub$ = new Subject<number>();
  private cartService: Cart;

  constructor(
    injector: Injector,
    readonly cartEventService: CartEventService,
    readonly authService: AuthService) {
    super();
    this.cartService = this.authService.isAnonymous() ?
      injector.get<Cart>(CartLocalService) :
      injector.get<Cart>(CartRemoteService);
  }

  ngOnChanges(): void {
    if (!this.loading) {
      this.inputFormControl = new UntypedFormControl();

      let multiplicity = this.product.multiplicityOrder;

      if (this.product.productStatusId === ProductStatuses.ORDER) {
        multiplicity = this.product.multiplicitySupplier;
      }

      if (multiplicity > 0) {
        this.multiplicityOrder = multiplicity;
      }

      this.inputFormControl.setValue(this.multiplicityOrder);
    }
  }

  ngOnInit(): void {
    this.cartEventService.getCart().pipe(
      map(cartProduct => {
        if (this.product) {
          const item = cartProduct.find(cartProduct => cartProduct && cartProduct.syncUid == this.product.syncUid);
          if (item) {
            this.detectChanges(item);
          }
        }
      }),
      takeUntil(this.unsubscribe)
    ).subscribe();

    this.inputSub$.pipe(
      debounceTime(800),
      takeUntil(this.unsubscribe),
      distinctUntilChanged(),
      tap(val => this.manual(val))
    ).subscribe();

  }

  ngAfterContentInit(): void {
    this.discount = Math.round((this.product.price - this.product.commercePrice.price) / this.product.price * 100);
    this.cdr.detectChanges();
  }

  public increase() {
    this.changeInputValue(this.inputFormControl.value + this.multiplicityOrder);
  }

  public decrease() {
    const res = this.inputFormControl.value - this.multiplicityOrder;

    if (res % this.multiplicityOrder === 0 && res > 0) {
      this.changeInputValue(res);
    }
  }

  public onKeyDown(event) {
    switch (event.key) {
      case 'ArrowUp':
        this.increase();
        return false;
      case 'ArrowDown':
        this.decrease();
        return false;
      case 'Enter':
        this.addToCart();
        break;
      case 'Escape':
        event.target.blur();
        return false;
    }
  }

  public manualChange() {
    this.inputSub$.next(this.inputFormControl.value);
  }

  private manual(value: number) {
    let res = Math.ceil(value / this.multiplicityOrder) * this.multiplicityOrder;
    if (res === 0) {
      res = this.multiplicityOrder;
    }
    this.changeInputValue(res);
  }

  private changeInputValue(value: number) {
    this.inputFormControl.setValue(value);
  }

  addToCart() {
    this.cartService.add(this.product, this.inputFormControl.value).pipe(
      takeUntil(this.unsubscribe),
      tap(item => this.detectChanges(item))
    ).subscribe();
  }

  detectChanges(cartProduct: CartProduct) {
    this.inCart = cartProduct === null ? 0 : cartProduct.amount;
    this.cdr.detectChanges();
  }

  showSpecificationPopup() {
    this.windowService.open(AddToSpecificationPopupComponent, {
      context: {
        product: this.product,
        amount: this.inputFormControl.value
      }
    });
  }
}
